import React, { Component } from 'react';
import Inherit from '../../../core/inherit';
import withHooks from '../../../core/withHooks';
import UserController from './user.controller';
import Page from '../../common/page/Page';
import { SCOPES, userHasScope } from '../../../constants/scopes.constants';
import SplitLayout from '../../common/layouts/splitLayout/SplitLayout';
import { SplitLayoutSide } from '../../common/layouts/splitLayout/splitLayout.constants';
import UserHeader from './elements/UserHeader';
import UserKpiCards from './elements/UserKpiCards';
import UserDetailsCard from './elements/UserDetailsCard';
import PayrollSyncStatusCard from './elements/PayrollSyncStatusCard';
import ReconcileAccountCard from './elements/ReconcileAccountCard';
import ReconcileAccountModal from './elements/ReconcileAccountModal';
import { UserActionConfirmModal } from '../../common/userActions';
import TransactionHistoryCard from './elements/TransactionHistoryCard';
import UserFunnelAnchor from './elements/UserFunnelAnchor';
import UserFunnelCard from './elements/UserFunnelCard';
import CreateTicketModal from '../../support/elements/CreateTicketModal';
import styles from './styles/user.module.scss';
import { DEFAULT_PAGE_SIZE, SYNC_DETAIL_IDLE } from '../../reports/reports.constants';

/**
 * User Journey detail page (`/users/user?userId=...`). Composes the
 * common `Page` shell with three Figma sections (5747:1664):
 *  - `UserHeader`           — breadcrumb, name, meta line, More actions + View as user.
 *  - `UserKpiCards`         — 3 KPI tiles (first one highlighted).
 *  - `SplitLayout`          — left rail with details + the sync status card
 *                             (whose dimensions expand to the active sync's
 *                             stored data, KAN-679); right column with
 *                             transaction history and, underneath it, the
 *                             onboarding journey.
 *
 * `UserFunnelCard` is fed by its own endpoint (an Athena scan) and carries
 * its own loading / failure states, so the rest of the page renders without
 * waiting on it.
 *
 * SplitLayout receives `sideVariant={SplitLayoutSide.LEFT}` so the
 * narrow rail lands on the left like the Figma reference (the
 * component defaults to the right side, which is what the auth
 * pages need). The 33/67 split itself comes from the page SCSS.
 *
 * @extends {UserController}
 * @extends {Component}
 */
class User extends Inherit(UserController, Component) {
    state = {
        loading: true,
        user: {},
        consumer: {},
        syncDetail: { ...SYNC_DETAIL_IDLE }, // same shape as the Reports panel (SyncDetail)
        // Feeds the header's payroll line only.
        payrollConnections: { loading: true, error: null, data: null, timezone: null },
        syncHistory: { loading: true, error: null, data: null, timezone: null },
        // Payroll Sync Status rows the admin has expanded, by dimension key.
        openSyncDimensions: {},
        // The header's payroll sources popover (multi-source members only).
        payrollSourcesOpen: false,
        isRestricted: false,
        restrictLoading: false,
        markCompleteLoading: false,
        deleteLastLoading: false,
        funnel: null,
        funnelLoading: false,
        funnelError: null,
        funnelRetryable: true,
        // The payroll-link failure history behind the funnel's error row. Its
        // own endpoint and its own Athena scan, so it stays empty until an
        // agent actually opens that row.
        linkAttemptsOpen: false,
        linkAttempts: null,
        linkAttemptsHasError: false,
        linkAttemptsLoading: false,
        linkAttemptsError: null,
        linkAttemptsRetryable: true,
        reconcileModalOpen: false,
        reconcileLoading: false,
        transactions: [],
        transactionsLoading: false,
        transactionsPageInfo: { current: 1, perPage: DEFAULT_PAGE_SIZE },
        // Controlled sort/filter for the transactions table. Both are applied
        // server-side; holding them here is what keeps the column headers
        // showing the ordering the server actually used. Null means the user has
        // not sorted — the endpoint still returns rows newest-first, but no
        // header claims to be the reason.
        transactionsSort: null,
        transactionsType: null,
        // The zone the ledger endpoint declared for the last payload; null
        // until the first response, when the table falls back to its default.
        transactionsTimezone: null,
        // A failed fetch is not an empty ledger. Kept apart so the table's
        // empty state can say which of the two it is.
        transactionsFailed: false,
        transactionTicketModalOpen: false,
        transactionTicketSubmitting: false,
        transactionTicketSubject: '',
        transactionTicketBody: '',
        transactionTicketAttachments: [],
        transactionTicketCategory: null,
        transactionTicketPriority: null,
        transactionTicketLedgerId: null,
        transactionTicketClientId: null,
    };

    render() {
        const {
            loading,
            consumer,
            markCompleteLoading,
            deleteLastLoading,
            funnel,
            funnelLoading,
            funnelError,
            funnelRetryable,
            linkAttemptsOpen,
            linkAttempts,
            linkAttemptsHasError,
            linkAttemptsLoading,
            linkAttemptsError,
            linkAttemptsRetryable,
            reconcileModalOpen,
            reconcileLoading,
            transactions,
            transactionsLoading,
            transactionsPageInfo,
            transactionsSort,
            transactionsType,
            transactionsTimezone,
            transactionsFailed,
            transactionTicketModalOpen,
            transactionTicketSubmitting,
            transactionTicketSubject,
            transactionTicketBody,
            transactionTicketAttachments,
            transactionTicketCategory,
            transactionTicketPriority,
        } = this.state;

        // Derive view labels through the controller so the helpers
        // stay testable in isolation and any handler that needs the
        // same values can reuse them via `this.<method>()`.
        const payPeriod = this.parsePayPeriod(consumer?.payPeriod);

        // Money presentation is resolved once here, through MoneyController, and
        // handed down. No child component decides a colour or a format.
        const repayment = this.describeRepaymentStatus(consumer);
        const formatMoney = (value) => this.formatMoney(value);
        const moneyClassName = (tone) => this.getMoneyColorClassName(tone);
        const toneForOwedAmount = (amount) => this.toneForOwedAmount(amount);
        const toneForSettledAmount = (amount) => this.toneForSettledAmount(amount);

        // Only the transaction-history ticket action is left on this flag; the
        // header and sync-card actions each have their own scope below.
        const canAction = userHasScope(this.data?.user, SCOPES.TEAM_INVITE);
        const canViewFunnel = this.canViewFunnel();
        const canReconcile = this.canReconcileAccount();
        const canDeleteSync = this.canDeleteSync();
        const canMarkSyncComplete = this.canMarkSyncComplete();
        const canViewStoredSyncData = this.canViewStoredSyncData();
        const payrollSyncStatusView = this.buildPayrollSyncStatusView();

        return (
            <Page
                isLoading={loading}
                scope={SCOPES.DASHBOARD_VIEW}
                notificationContextHolder={this.props.notificationContextHolder}
            >
                <div className={styles.page}>
                    <UserHeader
                        {...this.buildHeaderModel()}
                        onOpenSso={() => this.handleOpenSso()}
                        onPayrollSourcesOpenChange={(next, info) => this.handlePayrollSourcesOpenChange(next, info)}
                        onUserIdCopied={() => this.handleUserIdCopied()}
                        onUserIdCopyFailed={() => this.handleUserIdCopyFailed()}
                    />
                    {canViewFunnel ? (
                        <UserFunnelAnchor
                            funnel={funnel}
                            loading={funnelLoading}
                            error={funnelError}
                        />
                    ) : null}
                    <UserKpiCards
                        consumer={consumer}
                        repayment={repayment}
                        formatMoney={formatMoney}
                        moneyClassName={moneyClassName}
                        toneForOwedAmount={toneForOwedAmount}
                        toneForSettledAmount={toneForSettledAmount}
                    />
                    <SplitLayout
                        key="user-journey"
                        className={styles.splitRoot}
                        contentClassName={styles.splitContent}
                        sideClassName={styles.splitSide}
                        sideVariant={SplitLayoutSide.LEFT}
                        side={(
                            <>
                                <UserDetailsCard consumer={consumer} payPeriod={payPeriod} />
                                <PayrollSyncStatusCard
                                    view={payrollSyncStatusView}
                                    onRetry={() => this.reloadSyncStatus()}
                                    onToggleDimension={(key) => this.handleToggleSyncDimension(key)}
                                    onRetryStoredData={canViewStoredSyncData ? () => this.loadSyncHistory() : null}
                                    markCompleteLoading={markCompleteLoading}
                                    deleteLastLoading={deleteLastLoading}
                                    onMarkComplete={() => this.handleMarkSyncComplete()}
                                    onDeleteLast={() => this.handleDeleteLastSync()}
                                    canDeleteSync={canDeleteSync}
                                    canMarkSyncComplete={canMarkSyncComplete}
                                />
                                {canReconcile ? (
                                    <ReconcileAccountCard
                                        loading={reconcileLoading}
                                        onReconcile={() => this.handleOpenReconcileModal()}
                                    />
                                ) : null}
                            </>
                        )}
                    >
                        <TransactionHistoryCard
                            rows={transactions}
                            loading={transactionsLoading}
                            pageInfo={transactionsPageInfo}
                            onTableChange={this.handleTransactionsTableChange.bind(this)}
                            sort={transactionsSort}
                            typeFilter={transactionsType}
                            timezone={transactionsTimezone}
                            failed={transactionsFailed}
                            onRetry={() => this.retryTransactions()}
                            onOpenTicket={(record) => this.handleOpenTransactionTicket(record)}
                            canAction={canAction}
                        />
                        {canViewFunnel ? (
                            <UserFunnelCard
                                funnel={funnel}
                                loading={funnelLoading}
                                error={funnelError}
                                onRetry={funnelRetryable ? () => this.loadFunnel() : null}
                                attemptsOpen={linkAttemptsOpen}
                                attempts={linkAttempts}
                                attemptsHasError={linkAttemptsHasError}
                                attemptsLoading={linkAttemptsLoading}
                                attemptsError={linkAttemptsError}
                                onOpenAttempts={() => this.openLinkAttempts()}
                                onCloseAttempts={() => this.closeLinkAttempts()}
                                onRetryAttempts={
                                    linkAttemptsRetryable ? () => this.loadLinkAttempts() : null
                                }
                            />
                        ) : null}
                    </SplitLayout>
                    <CreateTicketModal
                        open={transactionTicketModalOpen}
                        submitting={transactionTicketSubmitting}
                        subject={transactionTicketSubject}
                        body={transactionTicketBody}
                        attachments={transactionTicketAttachments}
                        category={transactionTicketCategory}
                        priority={transactionTicketPriority}
                        title="New support ticket"
                        description="Create a support ticket linked to this transaction."
                        onSubjectChange={(value) => this.setState({ transactionTicketSubject: value })}
                        onBodyChange={(value) => this.setState({ transactionTicketBody: value })}
                        onAttachmentsChange={(entries) => this.setState({ transactionTicketAttachments: entries })}
                        onCategoryChange={(value) => this.setState({ transactionTicketCategory: value })}
                        onPriorityChange={(value) => this.setState({ transactionTicketPriority: value })}
                        onCancel={() => this.handleCloseTransactionTicketModal()}
                        onSubmit={() => this.handleSubmitTransactionTicket()}
                    />
                    <ReconcileAccountModal
                        open={reconcileModalOpen}
                        loading={reconcileLoading}
                        onCancel={() => this.handleCloseReconcileModal()}
                        onConfirm={() => this.handleConfirmReconcile()}
                    />
                    <UserActionConfirmModal
                        confirm={this.userActionConfirm}
                        onCancel={() => this.cancelUserAction()}
                        onConfirm={() => this.userActionConfirmSubmit()}
                    />
                </div>
            </Page>
        );
    }
}

export default withHooks(User);
